// Who can grant access, for every `NoAccess` (owner ui-primitives). Kept apart from NoAccess so the
// app root can provide it without loading the no-access screen, its styles and messages.
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

/** Someone who can grant access. */
export type AccessContact = { name: string; email?: string; initials?: string; color?: string };

/** Loads who can grant access in this workspace (the app reads its member directory). */
export type AccessContactsSource = () => Promise<readonly AccessContact[]>;

const ContactsContext = createContext<AccessContactsSource | null>(null);

/**
 * Gives every `NoAccess` below it the people who can grant access, so screens never wire them one
 * by one. The source is called when a NoAccess without `admins` renders (cache it in the source).
 */
export function AccessContactsProvider({
  source,
  children,
}: {
  source: AccessContactsSource;
  children: ReactNode;
}) {
  return <ContactsContext.Provider value={source}>{children}</ContactsContext.Provider>;
}

/**
 * The given contacts, else those of the nearest `AccessContactsProvider`: `undefined` while they
 * load, `[]` when nobody can be named.
 */
export function useAccessContacts(
  own: readonly AccessContact[] | undefined,
): readonly AccessContact[] | undefined {
  const source = useContext(ContactsContext);
  const [loaded, setLoaded] = useState<readonly AccessContact[] | undefined>(undefined);
  useEffect(() => {
    if (own || !source) return;
    let live = true;
    source().then(
      (contacts) => live && setLoaded(contacts),
      () => live && setLoaded([]),
    );
    return () => {
      live = false;
    };
  }, [own, source]);
  if (own) return own;
  return source ? loaded : [];
}
